import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DateTimeInput } from 'semantic-ui-calendar-react'
import { Form, Segment, Tab, TabPane } from 'semantic-ui-react'
import FarmerGroupsService from '~/api/services/farmerGroups'
import SurveyService from '~/api/services/survey'
import { FormFields } from '~/components/FormFields'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { RouteTypes } from '~/routes/constants'
import { StyledMenuItem } from '../CloudStorage/style'
import { getError } from '~/components/FormFields/util'
import { StyledErrorLabel } from '../MilkMoney/MilkDelivery/ShowTours/style'
import { getDateWithTime, getMinDate, handleErrors } from '~/utils/helpers'

const CreateEditSurvey: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const [options, setOptions] = useState(null)
  const navigate = useNavigate()
  const { handleSubmit, control, reset, getValues, setValue, watch, formState, formState: { isSubmitted, isDirty } } = useForm({
    mode: 'onSubmit'
  })
  const watchStartDate = watch('start')
  const watchEndDate = watch('end')

  useEffect(() => {
    fetchSurveyData()
  }, [])

  useEffect(() => {
    if (formState.isSubmitted) {
      if (Object.keys(formState.errors).length > 0) {
        notify(t('Please fill in all required fields'), 'error')
      }
    }

  }, [formState.isSubmitted])

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  async function fetchSurveyData() {
    setIsBusy(true);
    try {
      const responseOptions = await SurveyService.getSurveyOptions()
      const responseFarmersList = await FarmerGroupsService.getFarmerGroupsList('survey')
      setOptions({ ...responseOptions.data, farmersList: responseFarmersList.data.map(farmer => ({ value: farmer.id, text: farmer.name })) })
      const { languages } = responseOptions.data
      if (id) {
        const response = await SurveyService.getSurveyData(id)
        const languagesEdit = response.data.languages
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              title: '',
              description: ''
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        response.data.languages = languagesEdit
        response.data.start = dayjs(new Date(response.data.start)).format('DD.MM.YYYY HH:mm')
        response.data.end = dayjs(new Date(response.data.end)).format('DD.MM.YYYY HH:mm')
        reset(response.data)
      } else {
        const languagesNew = []
        languages.map((language) => {
          languagesNew.push({
            id: language.id,
            title: '',
            description: ''
          })
        })
        setValue('languages', languagesNew)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function convertDate(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    const time = yearAndTime?.split(' ')[1]
    return (`${year}-${month}-${day} ${time}`)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    data.start = convertDate(watchStartDate)
    data.end = convertDate(watchEndDate)
    try {
      if (id) {
        await SurveyService.updateSurvey(id, data)
        notify(t('Successfully updated survey'), 'success')
        navigate(RouteTypes.SURVEY_SHOW.replace(':id', id))
      } else {
        const response = await SurveyService.createSurvey(data)
        notify(t('Successfully created survey'), 'success')
        navigate(RouteTypes.SURVEY_SHOW.replace(':id', response.data.surveyId))
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderLanguagesForm() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={options?.languages?.map((language, languageIndex) => {
          const langIndex = getValues('languages').findIndex(langValue => langValue.id === language.id)
          const watchDescription = watch(`languages[${langIndex}].description`)
          return {
            menuItem: getMenuItem(language.label, langIndex),
            pane: <TabPane
              active={languageIndex === activeIndex}
              style={{ border: 'none' }}
              key={language?.id}
            >
              <FormFields.Input
                control={control}
                name={`languages[${langIndex}].title`}
                label={t('Title')}
                placeholder={t('Title')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter title')
                  },
                  maxLength: {
                    value: 100,
                    message: t('Max char. is 100')
                  }
                }}
              />
              <FormFields.TextArea
                control={control}
                name={`languages[${langIndex}].description`}
                label={t('Description')}
                style={{ marginBottom: 0 }}
                placeholder={t('Description')}
                rules={{
                  maxLength: {
                    value: 1000,
                    message: t('Max char. is 1000')
                  }
                }}
              />
              <small>{t('Characters: {{count}} (max 1000)', { count: watchDescription?.length || 0 })}</small>
            </TabPane>
          };
        })}
      />
    );
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderForm() {
    return <>
      <FormFields.Checkbox
        control={control}
        name={'reminder'}
        label={t('Reminder')}
        defaultValue={0}
        checkboxValue={1}
      />
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'start'}
          rules={{
            required: {
              value: true,
              message: t('Please enter start date')
            },
            validate: () => {
              const dateFrom = getDateWithTime(watchStartDate)
              const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
              if (dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0) {
                return t('Please select a date ahead of the current date.')
              }
              return true
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = watchStartDate ? getDateWithTime(watchStartDate) : ''
            const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
            return <Form.Field
              required
              error={!watchStartDate && isSubmitted ? getError(fieldState) : dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 ? true : false}
            >
              <label>{t('Start date')}</label>
              <DateTimeInput
                name='dateTime'
                placeholder={t('Start date')}
                minDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
                closable
                onKeyDown={handleKeyPress}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                className={'datePicker'}
                value={watchStartDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('start', value)}
              />
              {!watchStartDate && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter start date')}</StyledErrorLabel>}
              {dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the current date.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
        <Controller
          control={control}
          name={'end'}
          rules={{
            required: {
              value: true,
              message: t('Please enter end date')
            },
            validate: () => {
              const dateFrom = getDateWithTime(watchStartDate)
              const dateTo = getDateWithTime(watchEndDate)
              if (dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0) {
                return t('Please select a date ahead of the start date.')
              }
              return true
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = getDateWithTime(watchStartDate || '')
            const dateTo = getDateWithTime(watchEndDate || '')
            return <Form.Field
              required
              error={!watchEndDate && isSubmitted ? getError(fieldState) : false}
            >
              <label>{t('End date')}</label>
              <DateTimeInput
                name='dateTime'
                className={'datePicker'}
                placeholder={t('End date')}
                closable
                markColor={'blue'}
                onKeyDown={handleKeyPress}
                onKeyPress={handleKeyPress}
                initialDate={getMinDate(watchStartDate)}
                minDate={getMinDate(watchStartDate)}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                disabled={!watchStartDate}
                value={watchEndDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('end', value)}
              />
              {!watchEndDate && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter end date')}</StyledErrorLabel>}
              {dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the start date.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
      </Form.Group >
      <FormFields.Select
        width={8}
        control={control}
        name={'farmerGroups'}
        upward
        rules={{
          required: {
            value: true,
            message: t('Please select farmer groups')
          }
        }}
        multiple
        placeholder={t('Please select farmer group')}
        label={t('Farmer groups')}
        options={options?.farmersList || []}
      />
    </>
  }

  return <Layout
    isBusy={isBusy}
    onSubmit={handleSubmit(onSubmit)}
    action={id ? t('Update') : t('Save')}
    segmented
    close={RouteTypes.SURVEY}
    title={id ? t('Edit survey') : t('Create survey')}
  >
    {options && !isBusy && <>
      <Segment>
        {renderLanguagesForm()}
      </Segment>
      <Segment style={{ marginTop: '3em' }}>
        {renderForm()}
      </Segment>
    </>
    }
  </Layout>
}
export default CreateEditSurvey